import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Play,
  CheckCircle,
  Clock,
  BookOpen,
  Award,
  ArrowRight,
  ArrowLeft,
  Video,
  FileText
} from 'lucide-react';

interface TrainingModule {
  id: string;
  title: string;
  description: string;
  type: 'video' | 'document' | 'interactive' | 'quiz';
  duration: string;
  completed: boolean;
  required: boolean;
  score?: number | null;
}

interface TrainingStepProps {
  onComplete: () => void;
  onSkip: () => void;
  onPrevious: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  isRequired: boolean;
  estimatedTime: string;
}

export const TrainingStep: React.FC<TrainingStepProps> = ({
  onComplete,
  onSkip,
  onPrevious,
  canGoBack,
  isRequired,
  estimatedTime
}) => {
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [currentModule, setCurrentModule] = useState<TrainingModule | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTrainingModules();
  }, []);

  const loadTrainingModules = async () => {
    try {
      setLoading(true);
      // Mock training modules
      const mockModules: TrainingModule[] = [
        {
          id: 'welcome',
          title: 'Welcome to Vehix',
          description: 'Introduction to the platform and its core features',
          type: 'video',
          duration: '3 minutes',
          completed: false,
          required: true
        },
        {
          id: 'recall_management',
          title: 'Recall Management Basics',
          description: 'Understanding recalls, campaigns, and how to manage them effectively',
          type: 'interactive',
          duration: '8 minutes',
          completed: false,
          required: true
        },
        {
          id: 'technician_workflow',
          title: 'Technician Workflow',
          description: 'How to view assignments, update status, and complete tasks',
          type: 'video',
          duration: '5 minutes',
          completed: false,
          required: true
        },
        {
          id: 'safety_protocols',
          title: 'Safety Protocols',
          description: 'Important safety guidelines and procedures for recall work',
          type: 'document',
          duration: '4 minutes',
          completed: false,
          required: true
        },
        {
          id: 'platform_quiz',
          title: 'Platform Knowledge Quiz',
          description: 'Test your understanding of the platform features',
          type: 'quiz',
          duration: '3 minutes',
          completed: false,
          required: true
        }
      ];

      setModules(mockModules);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className='h-5 w-5' />;
      case 'document':
        return <FileText className='h-5 w-5' />;
      case 'interactive':
        return <Users className='h-5 w-5' />;
      case 'quiz':
        return <Award className='h-5 w-5' />;
      default:
        return <BookOpen className='h-5 w-5' />;
    }
  };

  const getModuleTypeColor = (type: string) => {
    switch (type) {
      case 'video':
        return 'text-red-600';
      case 'document':
        return 'text-blue-600';
      case 'interactive':
        return 'text-green-600';
      case 'quiz':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleModuleComplete = (moduleId: string) => {
    setModules(prev =>
      prev.map(module =>
        module.id === moduleId
          ? {
              ...module,
              completed: true,
              score: module.type === 'quiz' ? Math.floor(Math.random() * 20) + 80 : null
            }
          : module
      )
    );
  };

  const handleStartModule = (module: TrainingModule) => {
    setCurrentModule(module);
  };

  const handleCloseModule = () => {
    setCurrentModule(null);
  };

  const getCompletionPercentage = () => {
    const requiredModules = modules.filter(m => m.required);
    const completedRequired = requiredModules.filter(m => m.completed);
    return Math.round((completedRequired.length / requiredModules.length) * 100);
  };

  const canComplete = () => {
    const requiredModules = modules.filter(m => m.required);
    return requiredModules.every(m => m.completed);
  };

  const completionPercentage = getCompletionPercentage();

  if (loading) {
    return (
      <div className='space-y-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-200 rounded w-1/3 mb-4' />
          <div className='space-y-3'>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className='h-20 bg-gray-200 rounded' />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (currentModule) {
    return (
      <div className='space-y-6'>
        {/* Module Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className={`${getModuleTypeColor(currentModule.type)}`}>
              {getModuleIcon(currentModule.type)}
            </div>
            <div>
              <h3 className='text-xl font-semibold'>{currentModule.title}</h3>
              <p className='text-muted-foreground'>{currentModule.description}</p>
            </div>
          </div>
          <Button variant='outline' onClick={handleCloseModule}>
            Close
          </Button>
        </div>

        {/* Module Content */}
        <Card>
          <CardContent className='p-8 text-center'>
            <div className='space-y-4'>
              <div
                className={`mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center ${getModuleTypeColor(currentModule.type)}`}
              >
                {getModuleIcon(currentModule.type)}
              </div>
              <div>
                <h4 className='text-lg font-semibold mb-2'>{currentModule.title}</h4>
                <p className='text-muted-foreground mb-4'>{currentModule.description}</p>
                <div className='flex items-center justify-center gap-2 text-sm text-muted-foreground'>
                  <Clock className='h-4 w-4' />
                  Duration: {currentModule.duration}
                </div>
              </div>
            </div>

            {/* Mock Content */}
            <div className='mt-8 p-6 bg-muted/50 rounded-lg'>
              <p className='text-sm text-muted-foreground mb-4'>
                This is a mock training module. In a real implementation, this would contain:
              </p>
              <ul className='text-sm text-muted-foreground space-y-2 text-left max-w-md mx-auto'>
                {currentModule.type === 'video' && (
                  <>
                    <li>• Video player with training content</li>
                    <li>• Progress tracking</li>
                    <li>• Interactive elements</li>
                  </>
                )}
                {currentModule.type === 'document' && (
                  <>
                    <li>• Document viewer</li>
                    <li>• Highlighting and notes</li>
                    <li>• Reading progress</li>
                  </>
                )}
                {currentModule.type === 'interactive' && (
                  <>
                    <li>• Interactive scenarios</li>
                    <li>• Step-by-step guidance</li>
                    <li>• Practice exercises</li>
                  </>
                )}
                {currentModule.type === 'quiz' && (
                  <>
                    <li>• Multiple choice questions</li>
                    <li>• Real-time scoring</li>
                    <li>• Immediate feedback</li>
                  </>
                )}
              </ul>
            </div>

            <Button
              className='mt-6'
              onClick={() => {
                handleModuleComplete(currentModule.id);
                handleCloseModule();
              }}
            >
              Mark as Complete
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='text-center space-y-2'>
        <div className='flex items-center justify-center gap-2'>
          <Award className='h-6 w-6 text-primary' />
          <h3 className='text-xl font-semibold'>Training & Orientation</h3>
        </div>
        <p className='text-muted-foreground'>
          Complete the required training modules to get started
        </p>
        <Badge variant='outline'>{estimatedTime}</Badge>
      </div>

      {/* Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <BookOpen className='h-5 w-5' />
            Training Progress
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-center justify-between'>
            <span className='text-sm font-medium'>Overall Progress</span>
            <span className='text-sm text-muted-foreground'>{completionPercentage}%</span>
          </div>
          <Progress value={completionPercentage} className='h-2' />
          <div className='flex items-center justify-between text-sm'>
            <span className='text-muted-foreground'>
              {modules.filter(m => m.required && m.completed).length} of{' '}
              {modules.filter(m => m.required).length} required modules completed
            </span>
            <Badge variant={completionPercentage === 100 ? 'default' : 'secondary'}>
              {completionPercentage === 100 ? 'Complete' : 'In Progress'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Training Modules */}
      <div className='space-y-4'>
        {modules.map(module => (
          <Card
            key={module.id}
            className={module.completed ? 'border-green-200 bg-green-50/50' : ''}
          >
            <CardContent className='p-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-4'>
                  <div className={`${getModuleTypeColor(module.type)}`}>
                    {getModuleIcon(module.type)}
                  </div>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2'>
                      <h4 className='font-semibold'>{module.title}</h4>
                      {module.required && (
                        <Badge variant='destructive' className='text-xs'>
                          Required
                        </Badge>
                      )}
                      {module.completed && (
                        <Badge variant='default' className='bg-green-600 text-xs'>
                          <CheckCircle className='h-3 w-3 mr-1' />
                          Completed
                        </Badge>
                      )}
                    </div>
                    <p className='text-sm text-muted-foreground mt-1'>{module.description}</p>
                    <div className='flex items-center gap-4 mt-2 text-xs text-muted-foreground'>
                      <div className='flex items-center gap-1'>
                        <Clock className='h-3 w-3' />
                        {module.duration}
                      </div>
                      <Badge variant='outline' className='text-xs'>
                        {module.type}
                      </Badge>
                      {module.score && (
                        <span className='text-green-600 font-medium'>Score: {module.score}%</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  {module.completed ? (
                    <Badge variant='default' className='bg-green-600'>
                      <CheckCircle className='h-3 w-3 mr-1' />
                      Done
                    </Badge>
                  ) : (
                    <Button size='sm' onClick={() => handleStartModule(module)}>
                      <Play className='h-3 w-3 mr-1' />
                      Start
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Completion Status */}
      <div className='flex items-center gap-2 p-4 bg-muted/50 rounded-lg'>
        {canComplete() ? (
          <>
            <CheckCircle className='h-5 w-5 text-green-600' />
            <span className='text-sm text-green-600 font-medium'>
              All required training modules completed! You can proceed to the next step.
            </span>
          </>
        ) : (
          <>
            <Clock className='h-5 w-5 text-orange-600' />
            <span className='text-sm text-orange-600'>
              Please complete all required training modules to continue.
            </span>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-6 border-t'>
        <div className='flex gap-2'>
          {canGoBack && (
            <Button variant='outline' onClick={onPrevious}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Previous
            </Button>
          )}
        </div>

        <div className='flex gap-2'>
          {!isRequired && (
            <Button variant='ghost' onClick={onSkip}>
              Skip for now
            </Button>
          )}
          <Button onClick={onComplete} disabled={!canComplete()}>
            Complete Training
            <ArrowRight className='h-4 w-4 ml-2' />
          </Button>
        </div>
      </div>
    </div>
  );
};
